import type { CalendarEvent } from "@/features/calendar/types";

/**
 * Calculates the layout (width and position) for overlapping calendar events.
 * Events are positioned in columns to avoid overlaps, with earlier events on the left.
 *
 * @param events - Array of calendar events to layout
 * @returns Map of event IDs to their layout properties (width and left position as percentages)
 */
export function calculateEventLayout(
  events: CalendarEvent[],
): Map<string, { widthPercent: number; leftPercent: number }> {
  const layout = new Map<
    string,
    { widthPercent: number; leftPercent: number }
  >();

  if (events.length === 0) return layout;

  // Sort events by start time (earliest first), then by end time
  const sortedEvents = [...events].sort((a, b) => {
    const aStart = new Date(a.start_time).getTime();
    const bStart = new Date(b.start_time).getTime();
    if (aStart !== bStart) return aStart - bStart;
    if (a.end_time !== b.end_time)
      return new Date(a.end_time).getTime() - new Date(b.end_time).getTime();
    if (a.calendar_id !== b.calendar_id)
      return a.calendar_id.localeCompare(b.calendar_id);
    return a.id.localeCompare(b.id); // Tie-breaker for events with same start and end time
  });

  // Check if two events overlap in time
  const eventsOverlap = (e1: CalendarEvent, e2: CalendarEvent) => {
    const start1 = new Date(e1.start_time).getTime();
    const end1 = new Date(e1.end_time).getTime();
    const start2 = new Date(e2.start_time).getTime();
    const end2 = new Date(e2.end_time).getTime();
    return start1 < end2 && start2 < end1;
  };

  // Assign each event to a column (events in same column don't overlap)
  const eventColumns = new Map<string, number>();
  const columns: CalendarEvent[][] = [];

  for (const event of sortedEvents) {
    // Find the first available column where this event doesn't overlap
    let columnIndex = 0;
    let placed = false;

    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      const hasConflict = column.some((e) => eventsOverlap(e, event));
      if (!hasConflict) {
        column.push(event);
        columnIndex = i;
        placed = true;
        break;
      }
    }

    // Create a new column if no suitable column exists
    if (!placed) {
      columnIndex = columns.length;
      columns.push([event]);
    }

    eventColumns.set(event.id, columnIndex);
  }

  // Calculate layout for each event based on its overlapping group
  for (const event of sortedEvents) {
    // Find all events that overlap with this event
    const overlappingEvents = sortedEvents.filter((e) =>
      eventsOverlap(e, event),
    );

    // Determine the total number of columns needed for this overlapping group
    const maxColumn = Math.max(
      ...overlappingEvents.map((e) => eventColumns.get(e.id) || 0),
    );
    const totalColumns = maxColumn + 1;

    const columnIndex = eventColumns.get(event.id) || 0;
    // Stretch right over the columns this event does not collide with: a
    // short event beside a long one used to leave the long one at half
    // width for its whole length, and three-way overlaps squeezed everyone.
    let span = 1;
    for (let c = columnIndex + 1; c < totalColumns; c++) {
      if ((columns[c] || []).some((e) => eventsOverlap(e, event))) break;
      span++;
    }
    layout.set(event.id, {
      widthPercent: span / totalColumns,
      leftPercent: columnIndex / totalColumns,
    });
  }

  return layout;
}
